import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  Film, ArrowLeft, User, MapPin, Camera, Plus, X, 
  Save, Loader2, Languages, DollarSign, Tag, Upload, Image
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateTalentProfile = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  
  const [formData, setFormData] = useState({
    full_name: user?.name || '',
    age: '',
    gender: '',
    location: '',
    height: '',
    languages: [],
    phone: '',
    email: user?.email || '',
    look_tags: [],
    photos: [''],
    intro_video: '',
    showreel: '',
    budget_min: '',
    budget_max: '',
    experience_level: 'beginner',
    travel_willing: true
  });

  const [newLanguage, setNewLanguage] = useState('');

  useEffect(() => {
    fetchLookTags();
  }, []);

  const fetchLookTags = async () => {
    try {
      const response = await axios.get(`${API}/look-tags`);
      setAvailableTags(response.data.tags);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddLanguage = () => {
    if (newLanguage.trim() && !formData.languages.includes(newLanguage.trim())) {
      handleChange('languages', [...formData.languages, newLanguage.trim()]);
      setNewLanguage('');
    }
  };

  const handleRemoveLanguage = (lang) => {
    handleChange('languages', formData.languages.filter(l => l !== lang));
  };

  const handleTagToggle = (tag) => {
    const newTags = formData.look_tags.includes(tag)
      ? formData.look_tags.filter(t => t !== tag)
      : [...formData.look_tags, tag];
    handleChange('look_tags', newTags);
  };

  const handleAddPhotoField = () => {
    handleChange('photos', [...formData.photos, '']);
  };

  const handlePhotoChange = (index, value) => {
    const newPhotos = [...formData.photos];
    newPhotos[index] = value;
    handleChange('photos', newPhotos);
  };

  const handleRemovePhoto = (index) => {
    const newPhotos = formData.photos.filter((_, i) => i !== index);
    handleChange('photos', newPhotos.length > 0 ? newPhotos : ['']);
  };

  // Direct file upload to Cloudinary
  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    setError('');
    
    try {
      const uploadedUrls = [];
      
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          setError('Please upload only image files');
          continue;
        }
        
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('folder', 'talents');
        
        const response = await axios.post(`${API}/upload/image`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.url) {
          uploadedUrls.push(response.data.url);
        }
      }
      
      if (uploadedUrls.length > 0) {
        const currentPhotos = formData.photos.filter(p => p.trim() !== '');
        handleChange('photos', [...currentPhotos, ...uploadedUrls]);
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload images. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.full_name || !formData.age || !formData.gender || !formData.location) {
      setError('Please fill in all required fields (Name, Age, Gender, Location)');
      return;
    }

    setSaving(true);
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Clean up photos array - remove empty strings
      const cleanPhotos = formData.photos.filter(p => p.trim() !== '');
      
      const payload = {
        ...formData,
        age: parseInt(formData.age),
        photos: cleanPhotos,
        budget_min: formData.budget_min ? parseInt(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseInt(formData.budget_max) : null
      };

      await axios.post(`${API}/talents`, payload, { 
        headers, 
        withCredentials: true 
      });
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/talents');
      }, 2000);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Save className="w-10 h-10 text-green-400" />
          </div>
          <h2 className="text-3xl font-bold mb-2">Profile Created!</h2>
          <p className="text-muted-foreground mb-4">Your talent profile has been submitted for approval.</p>
          <p className="text-sm text-muted-foreground">Redirecting to talent directory...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 nav-glass">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate(-1)} 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>

            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <Film className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-display text-xl tracking-wide">CASTDECK</span>
            </Link>

            <div className="w-20"></div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Create Talent Profile</h1>
          <p className="text-muted-foreground">Fill in your details to join the CastDeck talent database</p>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 mb-6" data-testid="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info Section */}
          <div className="bg-card border border-border p-6">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Basic Information
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-sm font-medium mb-2 block">Full Name *</Label>
                <Input
                  value={formData.full_name}
                  onChange={(e) => handleChange('full_name', e.target.value)}
                  placeholder="Enter your full name"
                  className="bg-input/20 border-white/10"
                  required
                  data-testid="name-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Age *</Label>
                <Input
                  type="number"
                  value={formData.age}
                  onChange={(e) => handleChange('age', e.target.value)}
                  placeholder="e.g., 25"
                  min="1"
                  max="100"
                  className="bg-input/20 border-white/10"
                  required
                  data-testid="age-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Gender *</Label>
                <Select value={formData.gender} onValueChange={(v) => handleChange('gender', v)}>
                  <SelectTrigger className="bg-input/20 border-white/10" data-testid="gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="non-binary">Non-binary</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Location *</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="e.g., Mumbai, Delhi"
                  className="bg-input/20 border-white/10"
                  required
                  data-testid="location-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Height</Label>
                <Input
                  value={formData.height}
                  onChange={(e) => handleChange('height', e.target.value)}
                  placeholder="e.g., 5'8&quot;"
                  className="bg-input/20 border-white/10"
                  data-testid="height-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Experience Level</Label>
                <Select value={formData.experience_level} onValueChange={(v) => handleChange('experience_level', v)}>
                  <SelectTrigger className="bg-input/20 border-white/10" data-testid="experience-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="experienced">Experienced</SelectItem>
                    <SelectItem value="celebrity">Celebrity</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Phone</Label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="+91 98765 43210"
                  className="bg-input/20 border-white/10"
                  data-testid="phone-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="your@email.com"
                  className="bg-input/20 border-white/10"
                  data-testid="email-input"
                />
              </div>
            </div>
          </div>

          {/* Languages Section */}
          <div className="bg-card border border-border p-6">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Languages className="w-5 h-5 text-primary" />
              Languages
            </h2>
            
            <div className="flex gap-2 mb-4">
              <Input
                value={newLanguage}
                onChange={(e) => setNewLanguage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddLanguage())}
                placeholder="Add a language (e.g., Hindi, English)"
                className="bg-input/20 border-white/10"
                data-testid="language-input"
              />
              <Button type="button" onClick={handleAddLanguage} variant="outline" className="border-white/10">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {formData.languages.map((lang, index) => (
                <span key={index} className="flex items-center gap-2 px-3 py-1 bg-muted text-sm">
                  {lang}
                  <button type="button" onClick={() => handleRemoveLanguage(lang)} className="text-muted-foreground hover:text-foreground">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {formData.languages.length === 0 && (
                <span className="text-muted-foreground text-sm">No languages added yet</span>
              )}
            </div>
          </div>

          {/* Look Tags Section */}
          <div className="bg-card border border-border p-6">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Tag className="w-5 h-5 text-primary" />
              Look & Style Tags
            </h2>
            <p className="text-sm text-muted-foreground mb-4">Select tags that describe your look and style</p>
            
            <div className="flex flex-wrap gap-2">
              {availableTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => handleTagToggle(tag)}
                  className={`px-3 py-1.5 text-sm transition-colors ${
                    formData.look_tags.includes(tag)
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                  data-testid={`tag-${tag.toLowerCase().replace(' ', '-')}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Photos Section */}
          <div className="bg-card border border-border p-6">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              Photos
            </h2>
            
            {/* Direct File Upload */}
            <div className="mb-6">
              <div 
                className="border-2 border-dashed border-white/20 hover:border-primary/50 transition-colors p-8 text-center cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="file-upload-input"
                />
                {uploading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 text-primary animate-spin" />
                    <p className="text-muted-foreground">Uploading photos...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <Upload className="w-10 h-10 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Click to upload photos</p>
                      <p className="text-sm text-muted-foreground">JPG, PNG, WebP • Multiple files allowed</p>
                    </div>
                    <Button type="button" variant="outline" className="border-primary text-primary mt-2">
                      <Image className="w-4 h-4 mr-2" /> Select from Device
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Uploaded Photos Preview */}
            {formData.photos.filter(p => p.trim() !== '').length > 0 && (
              <div className="mb-6">
                <Label className="text-sm font-medium mb-3 block">Uploaded Photos ({formData.photos.filter(p => p.trim() !== '').length})</Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                  {formData.photos.filter(p => p.trim() !== '').map((photo, index) => (
                    <div key={index} className="relative group aspect-square">
                      <img 
                        src={photo} 
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover border border-border"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Error'; }}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(formData.photos.indexOf(photo))}
                        className="absolute top-1 right-1 p-1 bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* URL Input - Alternative */}
            <div className="border-t border-border pt-6">
              <Label className="text-sm font-medium mb-3 block text-muted-foreground">
                Or add photos via URL
              </Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Paste image URL here..."
                  className="bg-input/20 border-white/10"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const url = e.target.value.trim();
                      if (url) {
                        const currentPhotos = formData.photos.filter(p => p.trim() !== '');
                        handleChange('photos', [...currentPhotos, url]);
                        e.target.value = '';
                      }
                    }
                  }}
                  data-testid="photo-url-input"
                />
                <Button type="button" variant="outline" className="border-white/10" onClick={handleAddPhotoField}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Media Links Section */}
          <div className="bg-card border border-border p-6">
            <h2 className="text-xl font-semibold mb-6">Media Links</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-sm font-medium mb-2 block">Intro Video URL</Label>
                <Input
                  value={formData.intro_video}
                  onChange={(e) => handleChange('intro_video', e.target.value)}
                  placeholder="https://youtube.com/watch?v=..."
                  className="bg-input/20 border-white/10"
                  data-testid="intro-video-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Showreel URL</Label>
                <Input
                  value={formData.showreel}
                  onChange={(e) => handleChange('showreel', e.target.value)}
                  placeholder="https://vimeo.com/..."
                  className="bg-input/20 border-white/10"
                  data-testid="showreel-input"
                />
              </div>
            </div>
          </div>

          {/* Budget Section */}
          <div className="bg-card border border-border p-6">
            <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-primary" />
              Budget Range
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-sm font-medium mb-2 block">Minimum Budget (₹)</Label>
                <Input
                  type="number"
                  value={formData.budget_min}
                  onChange={(e) => handleChange('budget_min', e.target.value)}
                  placeholder="e.g., 50000"
                  className="bg-input/20 border-white/10"
                  data-testid="budget-min-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">Maximum Budget (₹)</Label>
                <Input
                  type="number"
                  value={formData.budget_max}
                  onChange={(e) => handleChange('budget_max', e.target.value)}
                  placeholder="e.g., 150000"
                  className="bg-input/20 border-white/10"
                  data-testid="budget-max-input"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.travel_willing}
                  onChange={(e) => handleChange('travel_willing', e.target.checked)}
                  className="w-5 h-5 bg-input border-border rounded"
                  data-testid="travel-checkbox"
                />
                <span>I am willing to travel for projects</span>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end gap-4 pt-4">
            <Button type="button" variant="outline" onClick={() => navigate(-1)} className="border-white/10">
              Cancel
            </Button>
            <Button type="submit" className="btn-gold px-8" disabled={saving} data-testid="submit-btn">
              {saving ? <Loader2 className="w-5 h-5 spinner mr-2" /> : <Save className="w-5 h-5 mr-2" />}
              Create Profile
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CreateTalentProfile;
